import type { ApiError as ApiErrorEnvelope } from '@sentinel/shared';
import { ApiError } from '../errors/api-error';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** A file upload: sent as multipart, the browser sets its content type and boundary. */
  form?: FormData;
  /** Skip the bearer token (login, refresh). */
  anonymous?: boolean;
  /** Skip the automatic refresh-and-retry on 401 (used by the refresh call itself). */
  noRetry?: boolean;
  /** Reuse a key across attempts of the same logical action; one is generated otherwise. */
  idempotencyKey?: string;
  signal?: AbortSignal | undefined;
  /** `text` or `blob`: the body as it came, never parsed (a file to save, even when JSON). */
  as?: 'text' | 'blob';
}

export interface TokenProvider {
  getAccessToken(): string | null;
  /** Obtains a fresh access token (rotating refresh cookie); resolves null when the session is gone. */
  refresh(): Promise<string | null>;
  onSessionLost(): void;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** One key per logical attempt, kept across the refresh-and-retry below and offered to the
 *  caller for a manual retry: the server replays the first answer instead of acting twice. */
function idempotencyKeyFor(method: string): string | null {
  if (!MUTATING.has(method)) return null;
  try {
    return crypto.randomUUID();
  } catch {
    return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

let tokenProvider: TokenProvider | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export function installTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/** Single-flight refresh: concurrent 401s share one refresh call. */
function refreshOnce(): Promise<string | null> {
  if (tokenProvider === null) return Promise.resolve(null);
  refreshInFlight ??= tokenProvider.refresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** The uniform error of an answer that is not ok: the server's envelope, or an internal error. */
export async function apiErrorFrom(response: Response): Promise<ApiError> {
  let envelope: ApiErrorEnvelope | null = null;
  try {
    envelope = (await response.json()) as ApiErrorEnvelope;
  } catch {
    envelope = null;
  }
  if (envelope === null || typeof envelope !== 'object' || !('error' in envelope)) {
    return new ApiError({
      status: response.status,
      category: 'internal',
      code: 'internal.unexpected',
      requestId: response.headers.get('x-request-id'),
    });
  }
  return new ApiError({
    status: response.status,
    category: envelope.error.category,
    code: envelope.error.code,
    requestId: envelope.error.requestId,
    details: envelope.error.details ?? [],
  });
}

async function perform(
  path: string,
  options: RequestOptions,
  token: string | null,
  idempotencyKey: string | null,
): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined && options.form === undefined)
    headers['content-type'] = 'application/json';
  if (token !== null) headers['authorization'] = `Bearer ${token}`;
  if (idempotencyKey !== null) headers['idempotency-key'] = idempotencyKey;
  const init: RequestInit = { method: options.method ?? 'GET', headers, credentials: 'include' };
  if (options.form !== undefined) init.body = options.form;
  else if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal !== undefined) init.signal = options.signal;
  try {
    return await fetch(`${BASE_URL}${path}`, init);
  } catch {
    throw ApiError.connection();
  }
}

/** Every call of the web goes through here: bearer token, JSON, uniform errors, silent refresh. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = options.anonymous === true ? null : (tokenProvider?.getAccessToken() ?? null);
  const method = options.method ?? 'GET';
  const idempotencyKey = options.idempotencyKey ?? idempotencyKeyFor(method);
  let response = await perform(path, options, token, idempotencyKey);

  if (response.status === 401 && options.anonymous !== true && options.noRetry !== true) {
    const fresh = await refreshOnce();
    if (fresh === null) {
      tokenProvider?.onSessionLost();
      throw await apiErrorFrom(response);
    }
    response = await perform(path, options, fresh, idempotencyKey);
  }

  if (!response.ok) throw await apiErrorFrom(response);
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (options.as === 'blob') return (await response.blob()) as unknown as T;
  if (options.as !== 'text' && contentType.includes('application/json'))
    return (await response.json()) as T;
  return (await response.text()) as unknown as T;
}
